import { getAccessTokenFromCookies, getRefreshTokenFromCookies, isTokenExpired, setAuthCookies, clearAuthCookies } from 'utils/authTokens';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

// ==============================|| AUTH API CLIENT ||============================== //

export async function loginApi({ identifier, password }) {
  if (!API_BASE_URL) {
    throw new Error('API base URL is not configured');
  }

  const normalizedIdentifier = String(identifier || '').trim();
  const credentials = /^\d{10}$/.test(normalizedIdentifier)
    ? { mobileNo: normalizedIdentifier, password }
    : { email: normalizedIdentifier, password };

  const response = await fetch(`${API_BASE_URL}/api/v1/auth/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(credentials)
  });

  let data;
  try {
    data = await response.json();
  } catch {
    throw new Error('Unable to parse server response');
  }

  if (!response.ok) {
    // Prefer server-provided message if available
    const message = data?.message || data?.error || 'Login failed';
    throw new Error(message);
  }

  return data;
}

export async function publicFetch(path, options = {}) {
  if (!API_BASE_URL) {
    throw new Error('API base URL is not configured');
  }

  const isFormData = options.body instanceof FormData;
  const headers = {
    ...(options.headers || {})
  };

  if (!isFormData) {
    const hasContentTypeHeader = Object.keys(headers).some((key) => key.toLowerCase() === 'content-type');
    if (!hasContentTypeHeader) {
      headers['Content-Type'] = 'application/json';
    }
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers
  });

  let data;
  try {
    data = await response.json();
  } catch {
    data = null;
  }

  if (!response.ok) {
    const message = data?.message || data?.error || 'Request failed';
    // Try to include more details for server (5xx) errors to aid debugging
    if (response.status >= 500) {
      try {
        const text = await response.text();
        // include a concise snippet of response body
        const snippet = (text || '').slice(0, 1000);
        // log full server response to console for debugging
        // eslint-disable-next-line no-console
        console.error('Server error', { url: `${API_BASE_URL}${path}`, status: response.status, body: text });
        throw new Error(`${message} (status ${response.status}) — ${snippet}`);
      } catch {
        throw new Error(`${message} (status ${response.status})`);
      }
    }

    throw new Error(message);
  }

  return data;
}

export async function refreshTokenApi(refreshToken) {
  if (!API_BASE_URL) {
    throw new Error('API base URL is not configured');
  }

  const response = await fetch(`${API_BASE_URL}/api/v1/auth/refresh-token`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ refreshToken })
  });

  let data;
  try {
    data = await response.json();
  } catch {
    throw new Error('Unable to parse server response');
  }

  if (!response.ok) {
    const message = data?.message || data?.error || 'Unable to refresh session';
    throw new Error(message);
  }

  if (data?.accessToken || data?.refreshToken) {
    setAuthCookies({ accessToken: data.accessToken, refreshToken: data.refreshToken ?? refreshToken });
  }

  return data;
}

function getAuthStateFromLocalStorage() {
  if (typeof window === 'undefined') return null;

  try {
    const raw = window.localStorage.getItem('dharbrothers-auth');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch {
    return null;
  }
}

// Generic helper for authenticated API calls.
// Usage: authorizedFetch('/api/v1/secure-endpoint', { method: 'GET' })
export async function authorizedFetch(path, options = {}) {
  if (!API_BASE_URL) {
    throw new Error('API base URL is not configured');
  }
  const { allowRefresh = true, ...fetchOptions } = options;

  const localAuth = getAuthStateFromLocalStorage();
  let accessToken = getAccessTokenFromCookies() || localAuth?.accessToken || null;
  const refreshToken = getRefreshTokenFromCookies() || localAuth?.refreshToken || null;

  // If there is no access token or it is expired, and refresh is allowed,
  // attempt to obtain a new access token via the refresh token endpoint.
  if (allowRefresh && refreshToken && (!accessToken || isTokenExpired(accessToken))) {
    try {
      const refreshed = await refreshTokenApi(refreshToken);
      accessToken = refreshed.accessToken;
    } catch {
      clearAuthCookies();
      throw new Error('Session expired. Please login again.');
    }
  } else if (accessToken && isTokenExpired(accessToken)) {
    // Access token expired. If refresh is allowed we clear cookies and
    // prompt re-login; otherwise allow the request to proceed with the
    // existing token (caller explicitly disabled refresh attempts).
    if (allowRefresh) {
      clearAuthCookies();
      throw new Error('Session expired. Please login again.');
    }
  }

  const isFormData = fetchOptions.body instanceof FormData;

  const headers = {
    ...(fetchOptions.headers || {}),
    ...(accessToken
      ? {
          Authorization: `Bearer ${accessToken}`
        }
      : {})
  };

  // For JSON requests, ensure we send the appropriate Content-Type header.
  // For FormData, let the browser set the multipart boundary automatically.
  if (!isFormData) {
    const hasContentTypeHeader = Object.keys(headers).some((key) => key.toLowerCase() === 'content-type');
    if (!hasContentTypeHeader) {
      headers['Content-Type'] = 'application/json';
    }
  }

  let response = await fetch(`${API_BASE_URL}${path}`, {
    ...fetchOptions,
    headers
  });

  if (allowRefresh && response.status === 401 && refreshToken) {
    try {
      const refreshed = await refreshTokenApi(refreshToken);
      const nextAccessToken = refreshed?.accessToken;
      if (nextAccessToken) {
        response = await fetch(`${API_BASE_URL}${path}`, {
          ...fetchOptions,
          headers: {
            ...headers,
            Authorization: `Bearer ${nextAccessToken}`
          }
        });
      }
    } catch {
      clearAuthCookies();
      throw new Error('Session expired. Please login again.');
    }
  }

  let data;
  try {
    data = await response.json();
  } catch {
    data = null;
  }

  if (!response.ok) {
    const message = data?.message || data?.error || 'Request failed';
    throw new Error(message);
  }

  return data;
}

// Variant of authorizedFetch that returns the raw Response instead of parsing JSON.
// Useful for downloading binary data like file attachments.
export async function authorizedFetchRaw(path, options = {}) {
  if (!API_BASE_URL) {
    throw new Error('API base URL is not configured');
  }
  const { allowRefresh = true, ...fetchOptions } = options;

  const localAuth = getAuthStateFromLocalStorage();
  let accessToken = getAccessTokenFromCookies() || localAuth?.accessToken || null;
  const refreshToken = getRefreshTokenFromCookies() || localAuth?.refreshToken || null;

  if (allowRefresh && refreshToken && (!accessToken || isTokenExpired(accessToken))) {
    try {
      const refreshed = await refreshTokenApi(refreshToken);
      accessToken = refreshed.accessToken;
    } catch {
      clearAuthCookies();
      throw new Error('Session expired. Please login again.');
    }
  } else if (accessToken && isTokenExpired(accessToken)) {
    if (allowRefresh) {
      clearAuthCookies();
      throw new Error('Session expired. Please login again.');
    }
  }

  const isFormData = fetchOptions.body instanceof FormData;

  const headers = {
    ...(fetchOptions.headers || {}),
    ...(accessToken
      ? {
          Authorization: `Bearer ${accessToken}`
        }
      : {})
  };

  if (!isFormData) {
    const hasContentTypeHeader = Object.keys(headers).some((key) => key.toLowerCase() === 'content-type');
    if (!hasContentTypeHeader) {
      headers['Content-Type'] = 'application/json';
    }
  }

  let response = await fetch(`${API_BASE_URL}${path}`, {
    ...fetchOptions,
    headers
  });

  if (allowRefresh && response.status === 401 && refreshToken) {
    try {
      const refreshed = await refreshTokenApi(refreshToken);
      const nextAccessToken = refreshed?.accessToken;
      if (nextAccessToken) {
        response = await fetch(`${API_BASE_URL}${path}`, {
          ...fetchOptions,
          headers: {
            ...headers,
            Authorization: `Bearer ${nextAccessToken}`
          }
        });
      }
    } catch {
      clearAuthCookies();
      throw new Error('Session expired. Please login again.');
    }
  }

  if (!response.ok) {
    let message = 'Request failed';
    try {
      const data = await response.json();
      message = data?.message || data?.error || message;
    } catch {
      // ignore JSON parse errors for non-JSON responses
    }
    throw new Error(message);
  }

  return response;
}

export async function getUserProfile() {
  return authorizedFetch('/api/v1/user/me/profile');
}

const CUSTOMER_AUTH_KEY = 'dharbrothers-customer-auth';

export function setCustomerAuthState({ accessToken, refreshToken }) {
  if (typeof window === 'undefined') return;
  const payload = {
    accessToken: accessToken || null,
    refreshToken: refreshToken || null
  };
  window.localStorage.setItem(CUSTOMER_AUTH_KEY, JSON.stringify(payload));
}

export function getCustomerAuthStateFromLocalStorage() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(CUSTOMER_AUTH_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch {
    return null;
  }
}

export function clearCustomerAuthState() {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(CUSTOMER_AUTH_KEY);
}

const CUSTOMER_PORTAL_SESSION_KEY = 'dharbrothers-customer-portal-session';

export function setCustomerPortalSession(session) {
  if (typeof window === 'undefined') return;
  if (!session || typeof session !== 'object') {
    window.localStorage.removeItem(CUSTOMER_PORTAL_SESSION_KEY);
    return;
  }
  window.localStorage.setItem(CUSTOMER_PORTAL_SESSION_KEY, JSON.stringify(session));
}

export function getCustomerPortalSession() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(CUSTOMER_PORTAL_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch {
    return null;
  }
}

export function clearCustomerPortalSession() {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(CUSTOMER_PORTAL_SESSION_KEY);
}

export async function authorizedCustomerFetch(path, options = {}) {
  if (!API_BASE_URL) {
    throw new Error('API base URL is not configured');
  }
  const { allowRefresh = true, ...fetchOptions } = options;

  const localAuth = getCustomerAuthStateFromLocalStorage();
  let accessToken = localAuth?.accessToken || null;
  const refreshToken = localAuth?.refreshToken || null;

  if (allowRefresh && refreshToken && (!accessToken || isTokenExpired(accessToken))) {
    try {
      const refreshed = await refreshTokenApi(refreshToken);
      accessToken = refreshed.accessToken;
      setCustomerAuthState({ accessToken: refreshed.accessToken, refreshToken: refreshed.refreshToken ?? refreshToken });
    } catch {
      throw new Error('Customer session expired. Please login again.');
    }
  } else if (accessToken && isTokenExpired(accessToken)) {
    if (allowRefresh) {
      throw new Error('Customer session expired. Please login again.');
    }
  }

  const isFormData = fetchOptions.body instanceof FormData;
  const headers = {
    ...(fetchOptions.headers || {}),
    ...(accessToken
      ? {
          Authorization: `Bearer ${accessToken}`
        }
      : {})
  };

  if (!isFormData) {
    const hasContentTypeHeader = Object.keys(headers).some((key) => key.toLowerCase() === 'content-type');
    if (!hasContentTypeHeader) {
      headers['Content-Type'] = 'application/json';
    }
  }

  let response = await fetch(`${API_BASE_URL}${path}`, {
    ...fetchOptions,
    headers
  });

  if (allowRefresh && response.status === 401 && refreshToken) {
    try {
      const refreshed = await refreshTokenApi(refreshToken);
      const nextAccessToken = refreshed?.accessToken;
      if (nextAccessToken) {
        response = await fetch(`${API_BASE_URL}${path}`, {
          ...fetchOptions,
          headers: {
            ...headers,
            Authorization: `Bearer ${nextAccessToken}`
          }
        });
        setCustomerAuthState({ accessToken: refreshed.accessToken, refreshToken: refreshed.refreshToken ?? refreshToken });
      }
    } catch {
      throw new Error('Customer session expired. Please login again.');
    }
  }

  let data;
  try {
    data = await response.json();
  } catch {
    data = null;
  }

  if (!response.ok) {
    const message = data?.message || data?.error || 'Request failed';
    throw new Error(message);
  }

  return data;
}

export async function authorizedCustomerFetchRaw(path, options = {}) {
  if (!API_BASE_URL) {
    throw new Error('API base URL is not configured');
  }
  const { allowRefresh = true, ...fetchOptions } = options;

  const localAuth = getCustomerAuthStateFromLocalStorage();
  let accessToken = localAuth?.accessToken || null;
  const refreshToken = localAuth?.refreshToken || null;

  if (allowRefresh && refreshToken && (!accessToken || isTokenExpired(accessToken))) {
    try {
      const refreshed = await refreshTokenApi(refreshToken);
      accessToken = refreshed.accessToken;
      setCustomerAuthState({ accessToken: refreshed.accessToken, refreshToken: refreshed.refreshToken ?? refreshToken });
    } catch {
      throw new Error('Customer session expired. Please login again.');
    }
  } else if (accessToken && isTokenExpired(accessToken)) {
    if (allowRefresh) {
      throw new Error('Customer session expired. Please login again.');
    }
  }

  const isFormData = fetchOptions.body instanceof FormData;
  const headers = {
    ...(fetchOptions.headers || {}),
    ...(accessToken
      ? {
          Authorization: `Bearer ${accessToken}`
        }
      : {})
  };

  if (!isFormData) {
    const hasContentTypeHeader = Object.keys(headers).some((key) => key.toLowerCase() === 'content-type');
    if (!hasContentTypeHeader) {
      headers['Content-Type'] = 'application/json';
    }
  }

  let response = await fetch(`${API_BASE_URL}${path}`, {
    ...fetchOptions,
    headers
  });

  if (allowRefresh && response.status === 401 && refreshToken) {
    try {
      const refreshed = await refreshTokenApi(refreshToken);
      const nextAccessToken = refreshed?.accessToken;
      if (nextAccessToken) {
        response = await fetch(`${API_BASE_URL}${path}`, {
          ...fetchOptions,
          headers: {
            ...headers,
            Authorization: `Bearer ${nextAccessToken}`
          }
        });
        setCustomerAuthState({ accessToken: refreshed.accessToken, refreshToken: refreshed.refreshToken ?? refreshToken });
      }
    } catch {
      throw new Error('Customer session expired. Please login again.');
    }
  }

  if (!response.ok) {
    let message = 'Request failed';
    try {
      const data = await response.json();
      message = data?.message || data?.error || message;
    } catch {
      // ignore JSON parse errors for non-JSON responses
    }
    throw new Error(message);
  }

  return response;
}
